import React, { useEffect, useRef, useState } from "react";
import {
  resolveMediaUrl,
  resolveMediaBackground,
  handleMediaImgError,
} from "../Helper/mediaUrl";
import {
  checkCommunityHandle,
  createSocialPost,
  fetchCommunitySettings,
  saveCommunityHandle,
  searchCommunityGifs,
} from "./communitySocialApi";

const EMOJI_CATEGORIES = {
  Recent: [],
  Smileys: ["😀", "😊", "😂", "🥹", "😍", "🥳", "😎", "🤔", "😢", "😡", "🤗", "😴"],
  Hearts: ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💕", "💞", "💖", "💗"],
  Hands: ["👍", "👎", "👏", "🙌", "🙏", "💪", "✌️", "🤝", "👋", "🤞", "🫡", "🫶"],
  Nature: ["🌟", "✨", "🔥", "🌈", "☀️", "🌙", "🌸", "🌿", "🕊️", "🦋", "🌊", "⭐"],
  Objects: ["🎉", "🎊", "🎁", "📢", "🎬", "📸", "🎵", "🏆", "💡", "📌", "✅", "✝️"],
};

const RECENT_KEY = "tpg_community_emoji_recent";
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/**
 * Unified Community post editor — text, multi image/GIF, Prayer Request, emoji, drag/drop/paste.
 * Video files are not uploaded; paste a YouTube URL for auto-embed.
 */
function CommunityCreatePostCard({
  profile = null,
  onProfileUpdate = null,
  onPosted = null,
  organizationId = null,
  creatorId = null,
  projectId = null,
  entityName = null,
  entityAvatar = null,
  entityPath = null,
  compact = false,
}) {
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [handleDraft, setHandleDraft] = useState("");
  const [handleMessage, setHandleMessage] = useState("");
  const [savingHandle, setSavingHandle] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [emojiCategory, setEmojiCategory] = useState("Smileys");
  const [emojiSearch, setEmojiSearch] = useState("");
  const [recentEmoji, setRecentEmoji] = useState([]);
  const [showGif, setShowGif] = useState(false);
  const [gifQuery, setGifQuery] = useState("");
  const [gifResults, setGifResults] = useState([]);
  const [gifMeta, setGifMeta] = useState(null);
  const [gifBusy, setGifBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [isPrayerRequest, setIsPrayerRequest] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const uploadAbortRef = useRef(false);

  const hasHandle = !!(profile && profile.has_handle && profile.handle);
  const postingAsEntity = !!(creatorId || organizationId || projectId);
  const canPost = postingAsEntity || hasHandle;
  const entityHandle = postingAsEntity
    ? (entityPath && String(entityPath).split("/").filter(Boolean).pop()) ||
      (entityName
        ? String(entityName)
            .toLowerCase()
            .replace(/[^a-z0-9_]+/g, "")
            .slice(0, 32)
        : null)
    : null;
  const displayName = postingAsEntity
    ? entityName || "This page"
    : (profile && profile.display_name) ||
      localStorage.getItem("active_profile_name") ||
      localStorage.getItem("username") ||
      "You";
  const avatar = postingAsEntity
    ? entityAvatar || null
    : (profile && profile.avatar) ||
      localStorage.getItem("active_profile_image") ||
      null;
  const coverPath = postingAsEntity
    ? null
    : (profile && (profile.cover || profile.community_cover)) || null;

  const maxAttachments = (settings && settings.max_attachments) || 10;
  const maxImageMb = (settings && settings.max_image_mb) || 8;
  const emojiEnabled = !settings || settings.emoji_enabled !== false;

  useEffect(() => {
    if (profile && profile.handle) setHandleDraft(profile.handle);
  }, [profile]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) setRecentEmoji(parsed.slice(0, 24));
    } catch (e) {
      /* ignore */
    }
    fetchCommunitySettings().then((res) => {
      if (!res.error && res.settings) setSettings(res.settings);
    });
  }, []);

  useEffect(() => {
    return () => {
      attachments.forEach((a) => {
        if (a.preview && a.preview.indexOf("blob:") === 0) {
          URL.revokeObjectURL(a.preview);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claimHandle = async (event) => {
    event.preventDefault();
    setSavingHandle(true);
    setHandleMessage("");
    const check = await checkCommunityHandle(handleDraft);
    if (!check.available) {
      setHandleMessage(check.reason || "Handle unavailable");
      setSavingHandle(false);
      return;
    }
    const result = await saveCommunityHandle(check.handle || handleDraft);
    setSavingHandle(false);
    if (result.error) {
      setHandleMessage(result.message || "Unable to save handle");
      return;
    }
    if (typeof onProfileUpdate === "function") onProfileUpdate(result.profile);
    setHandleMessage("Handle saved. You can post now.");
  };

  const revokeAttachment = (att) => {
    if (att.preview && att.preview.indexOf("blob:") === 0) {
      URL.revokeObjectURL(att.preview);
    }
  };

  const clearAttachments = () => {
    attachments.forEach(revokeAttachment);
    setAttachments([]);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const next = [];
      prev.forEach((a) => {
        if (a.id === id) revokeAttachment(a);
        else next.push(a);
      });
      return next;
    });
  };

  const addFiles = (fileList, preferredKind) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setError("");
    setAttachments((prev) => {
      const next = prev.slice();
      incoming.forEach((file) => {
        if (next.length >= maxAttachments) return;
        if (file.type.indexOf("video/") === 0) {
          setError("Video uploads are not supported. Paste a YouTube or Vimeo link in your post.");
          return;
        }
        const kind =
          preferredKind ||
          (file.type === "image/gif" ? "gif" : "image");
        if (IMAGE_TYPES.indexOf(file.type) < 0) {
          setError("Use JPG, PNG, GIF, or WebP images.");
          return;
        }
        const maxBytes = maxImageMb * 1024 * 1024;
        if (file.size > maxBytes) {
          setError(`Image must be ${maxImageMb}MB or smaller.`);
          return;
        }
        next.push({
          id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          kind,
          file,
          preview: URL.createObjectURL(file),
          source: "upload",
        });
      });
      return next;
    });
  };

  const pickMedia = (kind) => {
    if (!fileInputRef.current) return;
    fileInputRef.current.accept = IMAGE_TYPES.join(",");
    fileInputRef.current.dataset.kind = kind || "image";
    fileInputRef.current.multiple = true;
    fileInputRef.current.click();
  };

  const onFileChange = (event) => {
    const kind =
      (fileInputRef.current && fileInputRef.current.dataset.kind) || undefined;
    addFiles(event.target.files, kind);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    if (event.dataTransfer && event.dataTransfer.files) {
      addFiles(event.dataTransfer.files);
    }
  };

  const onPaste = (event) => {
    const items = event.clipboardData && event.clipboardData.items;
    if (!items) return;
    const files = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length) {
      event.preventDefault();
      addFiles(files);
    }
  };

  const rememberEmoji = (emoji) => {
    const next = [emoji].concat(recentEmoji.filter((e) => e !== emoji)).slice(0, 24);
    setRecentEmoji(next);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (e) {
      /* ignore */
    }
  };

  const insertEmoji = (emoji) => {
    rememberEmoji(emoji);
    const el = textareaRef.current;
    if (!el) {
      setBody((prev) => prev + emoji);
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const next = body.slice(0, start) + emoji + body.slice(end);
    setBody(next);
    window.setTimeout(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const loadGifs = async (q) => {
    setGifBusy(true);
    const data = await searchCommunityGifs(q || "happy");
    setGifBusy(false);
    setGifResults(Array.isArray(data.results) ? data.results : []);
    setGifMeta(data);
  };

  const openGifPicker = () => {
    setShowGif(true);
    setShowEmoji(false);
    if (!gifResults.length) loadGifs(gifQuery || "happy");
  };

  const selectGif = (gif) => {
    // Stub/demo GIF rows are not real media — do not attach (PR-007).
    if (!gif || gif.stub) {
      return;
    }
    if (attachments.length >= maxAttachments) {
      setError(`Max ${maxAttachments} attachments.`);
      return;
    }
    setAttachments((prev) =>
      prev.concat([
        {
          id: `gif-${gif.id || Date.now()}`,
          kind: "gif",
          source: "gif",
          preview: gif.preview_url || gif.url,
          gif,
        },
      ])
    );
    setShowGif(false);
  };

  const submitPost = async (event) => {
    event.preventDefault();
    if (!canPost) {
      setError("Create a public handle before posting.");
      return;
    }
    const text = body.trim();
    if (!text && !attachments.length) {
      setError("Write something or attach media before posting.");
      return;
    }
    uploadAbortRef.current = false;
    setSubmitting(true);
    setError("");
    setStatus("posting");
    setUploadProgress(attachments.length ? 15 : null);

    const files = attachments.filter((a) => a.file).map((a) => a.file);
    const gifs = attachments
      .filter((a) => a.source === "gif" && a.gif)
      .map((a) => ({
        url: a.gif.url,
        id: a.gif.id,
        provider: a.gif.provider || "giphy",
        preview_url: a.gif.preview_url,
        width: a.gif.width,
        height: a.gif.height,
      }));

    const optimistic = {
      id: `local-${Date.now()}`,
      kind: "post",
      body: text || "Shared media.",
      content: text,
      is_prayer_request: !!isPrayerRequest,
      created_at: new Date().toISOString(),
      timestamp: "Just now",
      author: {
        display_name: displayName,
        handle: postingAsEntity ? entityHandle : profile && profile.handle,
        avatar,
        cover: coverPath,
        community_cover: coverPath,
        path: postingAsEntity ? entityPath || null : profile && profile.path,
        identity: postingAsEntity
          ? creatorId
            ? "creator"
            : organizationId
              ? "organization"
              : "project"
          : "user",
      },
      media: attachments[0]
        ? { type: attachments[0].kind, path: attachments[0].preview }
        : null,
      attachments: attachments.map((a, idx) => ({
        id: null,
        type: a.kind,
        path: a.preview,
        sort_order: idx,
      })),
      images: attachments
        .filter((a) => a.kind === "image" || a.kind === "gif")
        .map((a) => ({ path: a.preview, type: a.kind })),
      counts: { like: 0, comment: 0, share: 0, prayer: 0 },
      viewer_reactions: [],
      actions: ["like", "prayer", "save"],
      _optimistic: true,
    };
    if (typeof onPosted === "function") onPosted(optimistic, { optimistic: true });

    if (attachments.length) setUploadProgress(55);
    if (uploadAbortRef.current) {
      setSubmitting(false);
      setUploadProgress(null);
      return;
    }

    const result = await createSocialPost({
      body: text,
      media: files.length ? files : undefined,
      gifs: gifs.length ? gifs : undefined,
      is_prayer_request: isPrayerRequest || undefined,
      organization_id: organizationId || undefined,
      creator_id: creatorId || undefined,
      project_id: projectId || undefined,
    });
    setSubmitting(false);
    setUploadProgress(attachments.length ? 100 : null);
    if (result.error) {
      setStatus(null);
      setUploadProgress(null);
      setError(result.message || "Unable to publish post");
      if (typeof onPosted === "function") onPosted(optimistic, { rollback: true });
      return;
    }
    setBody("");
    setIsPrayerRequest(false);
    clearAttachments();
    setStatus("success");
    setUploadProgress(null);
    if (typeof onPosted === "function") {
      onPosted(result.post, { replaceId: optimistic.id });
    }
    window.setTimeout(() => setStatus(null), 2200);
  };

  const cancelUpload = () => {
    uploadAbortRef.current = true;
    setSubmitting(false);
    setUploadProgress(null);
    setStatus(null);
    setError("Upload cancelled.");
  };

  const categoryEmojis =
    emojiCategory === "Recent"
      ? recentEmoji
      : EMOJI_CATEGORIES[emojiCategory] || [];
  const filteredEmojis = emojiSearch
    ? Object.keys(EMOJI_CATEGORIES)
        .filter((k) => k !== "Recent")
        .reduce((acc, key) => acc.concat(EMOJI_CATEGORIES[key]), [])
        .filter(() => true)
    : categoryEmojis;

  const coverStyle = coverPath
    ? { backgroundImage: resolveMediaBackground(coverPath) }
    : undefined;

  return (
    <section
      className={
        "community-create-post" +
        (compact ? " community-create-post--compact" : "") +
        (coverPath ? " community-create-post--cover" : "")
      }
      aria-labelledby="create-post-title"
    >
      <div
        className={
          "community-create-post__stage" +
          (coverPath ? " community-create-post__stage--cover" : "")
        }
        style={coverStyle}
      >
        <div className="community-create-post__header">
          <div className="community-create-post__avatar">
            {avatar ? (
              <img
                src={resolveMediaUrl(avatar)}
                alt=""
                onError={handleMediaImgError}
              />
            ) : (
              <span>{(displayName || "?").charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="community-create-post__identity">
            <h2 id="create-post-title" className="community-create-post__name">
              {displayName}
            </h2>
            {postingAsEntity && entityHandle ? (
              <p className="community-create-post__handle">@{entityHandle}</p>
            ) : hasHandle ? (
              <p className="community-create-post__handle">@{profile.handle}</p>
            ) : (
              <p className="community-create-post__handle community-create-post__handle--warn">
                Create a public handle to post
              </p>
            )}
          </div>
        </div>
      </div>

      {!postingAsEntity && !hasHandle ? (
        <form className="community-create-post__handle-gate" onSubmit={claimHandle}>
          <label htmlFor="community-handle-input" className="community-create-post__label">
            Choose your public handle
          </label>
          <div className="community-create-post__handle-row">
            <span className="community-create-post__at" aria-hidden="true">
              @
            </span>
            <input
              id="community-handle-input"
              className="community-create-post__handle-input"
              value={handleDraft}
              onChange={(e) => setHandleDraft(e.target.value.replace(/\s+/g, ""))}
              placeholder="yourname"
              autoComplete="off"
              maxLength={30}
              required
            />
            <button
              type="submit"
              className="btn btn-danger"
              disabled={savingHandle || !handleDraft.trim()}
            >
              {savingHandle ? "Saving…" : "Create handle"}
            </button>
          </div>
          {handleMessage ? (
            <p className="community-create-post__hint" role="status">
              {handleMessage}
            </p>
          ) : (
            <p className="community-create-post__hint">
              Letters, numbers, and underscores — 3 to 30 characters. Edit anytime
              from Community Profile.
            </p>
          )}
        </form>
      ) : (
        <form
          className={
            "community-create-post__form" +
            (dragOver ? " community-create-post__form--drag" : "")
          }
          onSubmit={submitPost}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <label htmlFor="community-post-body" className="sr-only">
            Write a post
          </label>
          <textarea
            id="community-post-body"
            ref={textareaRef}
            className="community-create-post__textarea"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onPaste={onPaste}
            placeholder={
              isPrayerRequest
                ? "Share a prayer request. You can also paste a YouTube link."
                : "Share an update. Paste a YouTube or Vimeo link to embed. Drop or paste images here."
            }
            disabled={submitting}
          />
          {isPrayerRequest ? (
            <p className="community-create-post__prayer-hint" role="status">
              This will post as a Prayer Request.
            </p>
          ) : null}

          {attachments.length ? (
            <div className="community-create-post__gallery">
              {attachments.map((att) => (
                <div key={att.id} className="community-create-post__preview">
                  {att.kind === "video" ? (
                    <video src={att.preview} controls playsInline />
                  ) : (
                    <img src={att.preview} alt="" />
                  )}
                  <button
                    type="button"
                    className="community-create-post__preview-remove"
                    onClick={() => removeAttachment(att.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {uploadProgress != null ? (
                <div
                  className="community-create-post__progress"
                  role="progressbar"
                  aria-valuenow={uploadProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <span style={{ width: `${uploadProgress}%` }} />
                  {submitting ? (
                    <button
                      type="button"
                      className="community-create-post__cancel"
                      onClick={cancelUpload}
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <input
            ref={fileInputRef}
            type="file"
            className="sr-only"
            multiple
            onChange={onFileChange}
          />

          <div className="community-create-post__toolbar">
            <div className="community-create-post__tools">
              <button
                type="button"
                className="community-create-post__tool"
                onClick={() => pickMedia("image")}
                disabled={submitting}
              >
                Image
              </button>
              <button
                type="button"
                className={
                  "community-create-post__tool" +
                  (isPrayerRequest ? " is-active" : "")
                }
                onClick={() => setIsPrayerRequest((v) => !v)}
                disabled={submitting}
                aria-pressed={isPrayerRequest}
              >
                Prayer Request
              </button>
              <button
                type="button"
                className="community-create-post__tool"
                onClick={openGifPicker}
                disabled={submitting}
                aria-expanded={showGif}
              >
                GIF
              </button>
              {emojiEnabled ? (
                <button
                  type="button"
                  className="community-create-post__tool"
                  onClick={() => {
                    setShowEmoji((v) => !v);
                    setShowGif(false);
                  }}
                  disabled={submitting}
                  aria-expanded={showEmoji}
                >
                  Emoji
                </button>
              ) : null}
            </div>
            <button
              type="submit"
              className="btn btn-danger community-create-post__submit"
              disabled={submitting || (!body.trim() && !attachments.length)}
            >
              {submitting ? "Posting…" : status === "success" ? "Posted" : "Post"}
            </button>
          </div>

          {showEmoji ? (
            <div className="community-create-post__emoji-panel" role="dialog" aria-label="Emoji picker">
              <div className="community-create-post__emoji-cats">
                {["Recent"].concat(Object.keys(EMOJI_CATEGORIES).filter((k) => k !== "Recent")).map(
                  (cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={
                        "community-create-post__emoji-cat" +
                        (emojiCategory === cat ? " is-active" : "")
                      }
                      onClick={() => {
                        setEmojiCategory(cat);
                        setEmojiSearch("");
                      }}
                    >
                      {cat}
                    </button>
                  )
                )}
              </div>
              <input
                className="community-create-post__emoji-search"
                placeholder="Search emoji…"
                value={emojiSearch}
                onChange={(e) => setEmojiSearch(e.target.value)}
              />
              <div className="community-create-post__emoji" role="listbox" aria-label="Emoji">
                {(emojiSearch
                  ? Object.keys(EMOJI_CATEGORIES)
                      .filter((k) => k !== "Recent")
                      .reduce((acc, key) => acc.concat(EMOJI_CATEGORIES[key]), [])
                  : filteredEmojis
                ).map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className="community-create-post__emoji-btn"
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
                {!filteredEmojis.length && emojiCategory === "Recent" ? (
                  <p className="community-create-post__hint">No recent emoji yet.</p>
                ) : null}
              </div>
            </div>
          ) : null}

          {showGif ? (
            <div className="community-create-post__gif-panel" role="dialog" aria-label="GIF picker">
              <div className="community-create-post__gif-search-row">
                <input
                  className="community-create-post__emoji-search"
                  placeholder="Search GIFs…"
                  value={gifQuery}
                  onChange={(e) => setGifQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      loadGifs(gifQuery);
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-default btn-sm"
                  onClick={() => loadGifs(gifQuery)}
                  disabled={gifBusy}
                >
                  {gifBusy ? "…" : "Search"}
                </button>
                <button
                  type="button"
                  className="btn btn-default btn-sm"
                  onClick={() => setShowGif(false)}
                >
                  Close
                </button>
              </div>
              {gifMeta && gifMeta.stub ? (
                <p className="community-create-post__hint">
                  {gifMeta.message || "GIF stub mode — configure API key in Admin."}
                </p>
              ) : null}
              <div className="community-create-post__gif-grid">
                {gifResults.map((gif) =>
                  gif.stub ? (
                    <span
                      key={gif.id}
                      className="community-create-post__gif-item community-create-post__gif-item--stub"
                      title={gif.title || "GIF unavailable"}
                      aria-disabled="true"
                    >
                      <span className="community-create-post__gif-stub">
                        {gif.title || "GIF unavailable"}
                      </span>
                    </span>
                  ) : (
                    <button
                      key={gif.id}
                      type="button"
                      className="community-create-post__gif-item"
                      onClick={() => selectGif(gif)}
                      title={gif.title || "GIF"}
                    >
                      <img
                        src={gif.preview_url || gif.url}
                        alt={gif.title || ""}
                      />
                    </button>
                  )
                )}
              </div>
            </div>
          ) : null}

          {error ? (
            <p className="community-create-post__error" role="alert">
              {error}
            </p>
          ) : null}
          {status === "success" ? (
            <p className="community-create-post__success" role="status">
              Your post is live in the feed.
            </p>
          ) : null}
        </form>
      )}
    </section>
  );
}

export default CommunityCreatePostCard;
